import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Saying out loud what only happened visually.
 *
 * Errors already announce themselves -- ErrorNote is role="alert". What had no
 * voice at all was everything that went RIGHT, and every route change: in a
 * single-page app the URL and the heading change while focus stays on the link
 * that was clicked, so a screen reader user is given no indication they have
 * arrived anywhere.
 *
 * Polite rather than assertive. None of this is urgent enough to interrupt
 * somebody mid-sentence, and an assertive region that fires on every navigation
 * is a region people turn off.
 */
let deliver = null;

export function announce(message) {
  deliver?.(message);
}

export function Announcer() {
  const [message, setMessage] = useState('');

  useEffect(() => {
    deliver = setMessage;
    return () => { deliver = null; };
  }, []);

  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

export function Loading({ rows = 3 }) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton" />)}
    </div>
  );
}

export function ErrorNote({ error, onRetry }) {
  if (!error) return null;

  return (
    <div className="alert error" role="alert">
      <div>{error.message}</div>
      {error.hint && <div className="small" style={{ marginTop: 4 }}>{error.hint}</div>}
      {onRetry && (
        <button type="button" className="small" style={{ marginTop: 8 }} onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * The one-time confirmation a screen was navigated to with.
 *
 * Committing a receipt hands its summary over as router state and lands here;
 * without this the numbers were passed and never read, so confirming a receipt
 * looked like nothing at all. Read once into local state and then cleared from
 * history, so a reload does not repeat old news.
 */
export function FlashNote() {
  const location = useLocation();
  const navigate = useNavigate();
  const [flash] = useState(location.state?.flash ?? null);

  useEffect(() => {
    if (location.state?.flash) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // Once, on arrival: the flash was already captured above.
  }, []);

  // A saved shelf check-up lands here too, and reads in its own numbers: how
  // many rows it confirmed, how many it added, and how many "maybes" it left
  // alone -- that last one is the point of the bucket, so it is said out loud.
  if (flash?.checkup !== undefined) {
    const said = [];
    if (flash.verified > 0) said.push(`checked ${flash.verified}`);
    if (flash.added > 0) said.push(`added ${flash.added}`);
    if (flash.removed > 0) said.push(`removed ${flash.removed}`);
    if (flash.maybe_ignored > 0) said.push(`${flash.maybe_ignored} maybe${flash.maybe_ignored === 1 ? '' : 's'} skipped`);
    return (
      <div className="alert info" role="status">
        {flash.checkup ? `${flash.checkup} checked` : 'Check-up saved'}
        {said.length > 0 ? ` — ${said.join(', ')}` : ''}.
      </div>
    );
  }

  if (!flash?.committed) return null;
  const parts = [];
  if (flash.inventory_added > 0) parts.push(`${flash.inventory_added} added to the pantry`);
  // M2/M3. A line checked off the shopping list already made its pantry row;
  // the receipt folds into it rather than adding a twin, and a receipt that
  // only folded would otherwise read as having added nothing. Rows the commit
  // closed because their expiry had passed are said too: the pantry shrank.
  if (flash.folded > 0) parts.push(`${flash.folded} already in the pantry from your list`);
  if (flash.auto_closed > 0) parts.push(`${flash.auto_closed} expired ${flash.auto_closed === 1 ? 'one' : 'ones'} closed`);
  if (flash.created_items > 0) parts.push(`${flash.created_items} new item${flash.created_items === 1 ? '' : 's'}`);
  if (flash.aliases_learned > 0) parts.push(`${flash.aliases_learned} shorthand learned`);
  // R4. The review screen sorted the lines by doubt; the flash echoes what that
  // came to, so "committed" has a shape. Four lines that wanted a decision
  // reads very differently from twenty-three that did not.
  const flagged = (flash.review_counts?.suggested ?? 0) + (flash.review_counts?.new ?? 0);
  if (flagged > 0) parts.push(`${flagged} that needed a look`);
  return (
    <div className="alert info" role="status">
      Receipt committed{parts.length > 0 ? ` — ${parts.join(', ')}` : ''}.
    </div>
  );
}

export function Empty({ title, children, action }) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      {children && <p className="small">{children}</p>}
      {action}
    </div>
  );
}

export function Pill({ tone = 'default', children }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

/**
 * Status pill for a recipe ingredient. `unknown` is deliberately its own tone:
 * it means the pantry could not be reconciled with the recipe's units, which
 * is different from "you don't have it".
 */
export function StatusPill({ status }) {
  const map = {
    have: ['ok', 'have it'],
    // M4. Its own word and its own tone, between "have it" and "can't tell":
    // the record says it is on the shelf, and the record is old enough that
    // the app will not spend the household's money on the strength of it.
    probably: ['warn', 'probably have'],
    short: ['warn', 'not enough'],
    missing: ['danger', 'need'],
    unknown: ['unknown', "can't tell"],
  };
  const [tone, label] = map[status] || ['default', status];
  return <Pill tone={tone}>{label}</Pill>;
}

/**
 * How sure the pantry is that a row is still there (M4).
 *
 * Silent when it is sure. A pill on every row is a pill nobody reads, and the
 * useful signal is the minority of rows where the answer is "probably" -- so
 * `present` renders nothing and the doubt is what catches the eye.
 */
export function PresencePill({ state, daysSinceVerified }) {
  if (state !== 'probably' && state !== 'doubtful') return null;
  const since = daysSinceVerified === null || daysSinceVerified === undefined
    ? null
    : ` · ${daysSinceVerified}d`;
  return (
    <Pill tone={state === 'doubtful' ? 'unknown' : 'warn'}>
      {state === 'doubtful' ? 'not checked' : 'probably'}{since}
    </Pill>
  );
}

/** Everything focusable inside a container, in tab order. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]),'
  + ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ title, onClose, children, footer }) {
  const ref = useRef(null);

  useEffect(() => {
    // Where focus came from, so it can go back. Without this, closing a sheet
    // drops focus to the top of the document and a keyboard user has to walk
    // the whole page again to get back to the control they just used.
    const opener = document.activeElement;

    const onKey = (event) => {
      if (event.key === 'Escape') { onClose(); return; }
      if (event.key !== 'Tab') return;

      // Tab was leaving the dialog while aria-modal told assistive tech the
      // rest of the page did not exist -- so focus went somewhere the user had
      // been told was not there, over content they could not scroll.
      const items = [...(ref.current?.querySelectorAll(FOCUSABLE) ?? [])]
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    // Stop the page behind the sheet from scrolling on touch devices.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // The first thing inside, rather than the dialog box itself: landing on the
    // container means the next Tab is the first control anyway, and a screen
    // reader announces the dialog either way.
    const firstInside = ref.current?.querySelector(FOCUSABLE);
    (firstInside ?? ref.current)?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
      // Only if it is still there. A sheet that closed because the thing behind
      // it was deleted has nowhere to put focus back.
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="icon ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
        {footer && <div className="row" style={{ marginTop: 16 }}>{footer}</div>}
      </div>
    </div>
  );
}

/** Today as YYYY-MM-DD in the user's own calendar, for date-input defaults.
 * NOT toISOString().slice(0, 10): that is UTC's calendar, which from an
 * American evening is already tomorrow. */
export function todayLocal() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** Formats a date as a short relative phrase; falls back to the raw value. */
export function relativeDate(value) {
  if (!value) return null;
  // A bare date is a calendar day, not an instant. Parsed as UTC midnight it
  // sat far enough in the past by any American afternoon that something bought
  // today rendered as "bought yesterday" -- so calendar days are compared to
  // calendar days, in the reader's own timezone. Timestamps stay instants.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  let date;
  let days;
  if (dateOnly) {
    date = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]);
    if (Number.isNaN(date.getTime())) return value;
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    days = Math.round((midnight.getTime() - date.getTime()) / 86_400_000);
  } else {
    date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
    if (Number.isNaN(date.getTime())) return value;
    days = Math.round((Date.now() - date.getTime()) / 86_400_000);
  }
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days === -1) return 'tomorrow';
  if (days > 1 && days < 30) return `${days}d ago`;
  if (days < -1 && days > -30) return `in ${-days}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ExpiryPill({ daysLeft }) {
  if (daysLeft === null || daysLeft === undefined) return null;
  if (daysLeft < 0) return <Pill tone="danger">expired {-daysLeft}d ago</Pill>;
  if (daysLeft === 0) return <Pill tone="danger">expires today</Pill>;
  if (daysLeft <= 3) return <Pill tone="warn">{daysLeft}d left</Pill>;
  if (daysLeft <= 7) return <Pill>{daysLeft}d left</Pill>;
  return null;
}

/**
 * A quantity input's value as a number, or undefined for blank or unparseable.
 *
 * Number('') is 0, and 0 is a legitimate stored quantity in this app -- so the
 * coercion silently turned an emptied field into "we have none", on five
 * screens. Undefined instead: JSON drops the key, a PATCH leaves the column
 * alone, and the add routes fall back to their own default of 1. A typed 0
 * still goes through, because it was typed.
 */
export function typedQty(raw) {
  const text = String(raw ?? '').trim();
  if (text === '') return undefined;
  const quantity = Number(text);
  return Number.isNaN(quantity) ? undefined : quantity;
}

/** Quantity + unit, hiding the noise of "1 each". */
export function formatQty(quantity, unit) {
  if (quantity === null || quantity === undefined) return '';
  const rounded = Math.round(Number(quantity) * 100) / 100;
  if (!unit || unit === 'each') return String(rounded);
  return `${rounded} ${unit}`;
}
